import { Link } from "react-router-dom";
import { Mail, ShieldAlert } from "lucide-react";
import { Logo } from "./Reveal";
import { scrollToId } from "@/lib/api";

export default function Footer() {
    return (
        <footer data-testid="site-footer">
            <section id="disclaimer" className="border-t border-slate-200/70 bg-slate-50 py-16 sm:py-20">
                <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
                    <div className="rounded-2xl border border-slate-200 bg-white p-8 sm:p-10" data-testid="legal-disclaimer">
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
                                <ShieldAlert className="h-5 w-5" />
                            </span>
                            <h2 className="font-display text-lg font-bold tracking-tight text-slate-900">Important disclaimer</h2>
                        </div>
                        <div className="mt-6 space-y-4 text-sm leading-relaxed text-slate-600">
                            <p>
                                Institutional Trend Flow (ITF) is a technical analysis indicator — a software tool provided
                                strictly for educational and informational purposes. Nothing on this website, in our
                                communications, or displayed by the indicator constitutes investment, trading or financial
                                advice, or a recommendation to enter into any transaction of any kind. ITF is not a signal
                                service and does not tell you what to trade or when to trade it.
                            </p>
                            <p>
                                Trading and investing involve substantial risk of loss and are not suitable for every
                                person. Past performance — including any simulated chart demonstrations on this page — is
                                not indicative of future results. You are solely responsible for your own trading
                                decisions, and <span className="font-semibold text-slate-900">we will not be responsible for any losses</span>{" "}
                                arising from the use of this indicator.
                            </p>
                            <p>
                                All sales are final. <span className="font-semibold text-slate-900">No refunds are given in any case</span>,
                                to the maximum extent permitted by law, as access is digital and granted to your personal
                                TradingView account. By purchasing, you acknowledge and accept our{" "}
                                <Link to="/terms" className="font-semibold text-emerald-700 hover:text-emerald-800">Terms &amp; Conditions</Link>,{" "}
                                <Link to="/refund-policy" className="font-semibold text-emerald-700 hover:text-emerald-800">Refund Policy</Link> and{" "}
                                <Link to="/privacy" className="font-semibold text-emerald-700 hover:text-emerald-800">Privacy Policy</Link> in full.
                            </p>
                            <p className="text-xs text-slate-400">
                                TradingView® is a registered trademark of TradingView, Inc. TradingView has no affiliation
                                with Institutional Trend Flow.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <div className="bg-white py-12">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col items-center justify-between gap-8 sm:flex-row sm:items-start">
                        <div>
                            <Logo />
                            <p className="mt-4 font-display text-sm font-semibold italic text-slate-500">Made by traders, for traders.</p>
                            <a
                                href="mailto:support@getitf.xyz"
                                data-testid="footer-support-email"
                                className="mt-3 flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-emerald-700"
                            >
                                <Mail className="h-4 w-4" /> support@getitf.xyz
                            </a>
                        </div>
                        <nav className="flex flex-wrap items-center justify-center gap-x-7 gap-y-2">
                            {[
                                { label: "Pricing", href: "#pricing" },
                                { label: "FAQ", href: "#faq" },
                                { label: "Disclaimer", href: "#disclaimer" },
                            ].map((l) => (
                                <button
                                    key={l.href}
                                    data-testid={`footer-link-${l.label.toLowerCase()}`}
                                    onClick={() => scrollToId(l.href)}
                                    className="text-sm text-slate-500 transition-colors hover:text-slate-900"
                                >
                                    {l.label}
                                </button>
                            ))}
                        </nav>
                        <nav className="flex flex-col items-center gap-2 sm:items-end">
                            <Link to="/terms" data-testid="footer-link-terms" className="text-sm text-slate-500 transition-colors hover:text-slate-900">
                                Terms &amp; Conditions
                            </Link>
                            <Link to="/refund-policy" data-testid="footer-link-refund" className="text-sm text-slate-500 transition-colors hover:text-slate-900">
                                Refund Policy
                            </Link>
                            <Link to="/privacy" data-testid="footer-link-privacy" className="text-sm text-slate-500 transition-colors hover:text-slate-900">
                                Privacy Policy
                            </Link>
                        </nav>
                    </div>
                    <div className="mt-10 border-t border-slate-100 pt-6 text-center">
                        <p className="font-mono text-[11px] text-slate-400">© 2026 Institutional Trend Flow. All rights reserved.</p>
                        <p className="mt-1.5 font-mono text-[10px] text-slate-400">
                            Payments processed by Dodo Payments — authorized reseller and Merchant of Record.
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
